/**
 * istanbul ignore file
 *
 * @format
 */

import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
	Component,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	TemplateRef,
	viewChild
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { chain } from '@core/chain';
import { Store as NgRxStore } from '@ngrx/store';
import {
	changeHeaderServiceStoreInfo,
	setUserStoreInfoFromCookie,
	setUserStoreName,
	setUserZipCode
} from '@pdp/data-access/state';
import { ProductList, ProductListCore } from '@plp/data-access/interfaces';
import {
	refreshProductList,
	selectFeatureFlags,
	selectIsCompareGridOpen,
	selectProductListCore
} from '@plp/data-access/state';
import { MetaService } from '@seo/data-access';
import { AuthenticationService } from '@shared/authentication/data-access';
import {
	ProductSelectionStateService,
	ProductListMlService,
	ProductListCompareService
} from '@plp/data-access/services';
import { analyticsSortMethodValues } from '@plp/data-access/constants';
import { setCommonData, setCommonDsgVhHeader } from '@state/data-access';
import { Customer } from '@shared/data-access/interfaces';
import {
	CartService,
	CookieService,
	HeaderLocationService,
	KillswitchService,
	Store,
	StoreService,
	UtilityService,
	VirtualHostCookieService,
	WINDOW
} from '@shared/data-access/services';

import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { Subject, of, takeUntil } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';

declare const BUILD_VERSION: string;
@Component({
	selector: 'plp-srlp-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();
	/**
	 * @description
	 * The Customer object from application state
	 */
	private readonly customer$ = this.store.select('CUSTOMER').pipe(takeUntil(this.destroy$));
	readonly featureFlags = this.ngrxStore.selectSignal(selectFeatureFlags);
	chainIdentifierAbbr = chain.chainIdentifierAbbr;
	chainMarketingIdentifierAbbr = chain.chainMarketingIdentifierAbbr;
	feedbackSurveyId = chain.feedbackSurveyId;
	/**
	 * @description
	 * launch flag used to hide/show things in ticker and footer
	 */
	isSecureLaunch = false;

	/**
	 * @description
	 * Used to determine whether or not the ticker name has been set to the product ID
	 */
	isTickerPageNameSet = false;
	ranAlready = false;
	sddZipCode = '';
	compareGridEnabled = false;
	compareCheckboxEnabled = false;
	modalContent = viewChild.required<TemplateRef<unknown>>('modalContent');
	isCompareGridOpen = this.ngrxStore.selectSignal(selectIsCompareGridOpen);

	constructor(
		@Inject(DOCUMENT) private document: Document,
		@Inject(PLATFORM_ID) private platformId,
		@Inject(WINDOW) private window: Window,
		private activatedRoute: ActivatedRoute,
		private analyticsService: ProductAnalyticsService,
		private authService: AuthenticationService,
		private cartService: CartService,
		private cookieService: CookieService,
		private headerLocationService: HeaderLocationService,
		private killSwitchService: KillswitchService,
		private metaService: MetaService,
		private ngrxStore: NgRxStore,
		private readonly plpCompareService: ProductListCompareService,
		private plpMlService: ProductListMlService,
		private productSelectionStateService: ProductSelectionStateService,
		private router: Router,
		private store: Store,
		private storeService: StoreService,
		private utilityService: UtilityService,
		private virtualHostCookieService: VirtualHostCookieService
	) {
		// Check authentication status and set up authentication status stream
		this.authService.localAuthSetup();

		// set dsg_vh in the common state
		this.ngrxStore.dispatch(setCommonData());
		this.ngrxStore.dispatch(setCommonDsgVhHeader({ dsgVh: this.cookieService.get('DSG_VH') }));

		// Reset the analytics
		if (!this.window.siteData) {
			this.analyticsService.resetAnalytics();
		}

		// Check to see if this has been defined
		if (typeof BUILD_VERSION === 'string' && BUILD_VERSION.trim() !== '') {
			// Set the build version on the global window
			this.window.BUILD_VERSION = BUILD_VERSION;
		}

		/*
		 * Only execute on browser
		 */
		if (isPlatformBrowser(this.platformId)) {
			// eliminate 'persistent' click focus
			document.body.addEventListener('mousedown', () => {
				document.body.classList.add('mouse-Event');
			});

			document.body.addEventListener('keydown', () => {
				document.body.classList.remove('mouse-Event');
			});

			// Initialize the Cart Service
			this.cartService.initialize();

			// Check cookie and populate Store
			this.authService.checkIfReturningCustomer();

			// Set default store
			this.storeService.initialize();

			/*
			 * DSG_UID cookie
			 */
			if (!this.cookieService.check('DSG_UID')) {
				// Create a DSG_UID cookie
				this.cookieService.set(
					'DSG_UID',
					this.utilityService.generateUUID(),
					365,
					'/',
					'',
					''
				);
			}

			// Virtual Host chain identifier
			this.virtualHostCookieService.setVirtualHostCookie();
		}

		this.killSwitchService.getKillSwitchesEnvironment$().subscribe((data) => {
			this.store.set('ENVIRONMENT_KILLSWITCH_VALUES', data);
		});
	}

	ngOnInit(): void {
		// Subscribe to the router events and filter to only `NavigationEnd`
		this.router.events
			.pipe(
				takeUntil(this.destroy$),
				filter((event) => event instanceof NavigationEnd),
				map(() => this.activatedRoute),
				map((route) => {
					while (route.firstChild) {
						route = route.firstChild;
					}
					return route;
				}),
				mergeMap((route: ActivatedRoute) =>
					of({
						data: route.data,
						params: route.params,
						queryParams: route.queryParams
					})
				)
			)
			.subscribe((route: any) => {
				this.routerSubscriptionHandler(route);
			});
		this.setStoreInfoZipCodeToStore();

		if (this.featureFlags()?.compareGridEnabled) {
			this.plpCompareService.initCompareGrid(this.modalContent());
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private routerSubscriptionHandler(route: any): void {
		// Get a Promise for route.data
		const dataPromise = route.data.pipe(take(1)).toPromise();

		// Get a Promise for route.params
		const paramsPromise = route.params.pipe(take(1)).toPromise();

		// Get a Promise for route.queryParams
		const queryParamsPromise = route.queryParams.pipe(take(1)).toPromise();

		// Wait until all of the Promises have been resolved
		Promise.all([dataPromise, paramsPromise, queryParamsPromise]).then((data) => {
			// Get the current query parameters from the route query params
			const CURRENT_QUERY_PARAMS = data[2];

			// Only execute in the Browser
			if (isPlatformBrowser(this.platformId)) {
				// Get the current Date/Time
				const CURRENT_DATETIME = new Date();

				// Set the chain
				this.analyticsService.setChain(chain.chainName);

				// Set the Error ID (if present)
				this.analyticsService.setErrorId();

				// Set the Error Message (if present)
				this.analyticsService.setErrorMessage();

				// Set the referring URL
				this.analyticsService.setReferringPageUrl(this.document.referrer);

				// Set the server date
				this.analyticsService.setServerDate(
					this.analyticsService.getFormattedDate(CURRENT_DATETIME)
				);

				// Set the server time
				this.analyticsService.setServerTime(
					this.analyticsService.getFormattedTime(CURRENT_DATETIME)
				);

				// Set the base Search properties
				this.analyticsService.setSearchPageBaseProperties({
					searchDetails: {
						DYMTerm: CURRENT_QUERY_PARAMS.dymterm || CURRENT_QUERY_PARAMS.DYMTerm || '',
						DYMType:
							CURRENT_QUERY_PARAMS.didyoumean ||
							CURRENT_QUERY_PARAMS.didYouMean ||
							'',
						replacementTerm: CURRENT_QUERY_PARAMS.replacementTerm || '',
						searchTerm:
							CURRENT_QUERY_PARAMS.enteredSearchTerm ||
							CURRENT_QUERY_PARAMS.searchTerm ||
							'',
						searchType: CURRENT_QUERY_PARAMS.DSGsearchType || ''
					}
				});

				if (!this.ranAlready) {
					const isFamilyPage =
						this.productSelectionStateService.getPageTypeData() === 'plp';
					const pageType = isFamilyPage ? 'Family' : 'Search';
					this.analyticsService.setType('page', pageType);
					this.setFamilySRLPAnalyticsData();
					this.ranAlready = true;
				}

				// Get the Customer from state and populate related analytics
				this.customer$.subscribe((customer: Customer) => {
					// Set the Loyalty ID
					this.analyticsService.setLoyaltyID(customer.x_acNumber || '');

					// Set the LoyaltyBalance
					this.analyticsService.setLoyaltyPointsBalance(customer.x_acBalancePoints || '');

					// Set the session ID
					this.analyticsService.setSessionID(customer.sessionId);

					// Set the user ID
					this.analyticsService.setUserID(customer.userId);

					// Set the visits count
					this.analyticsService.setVisitNumber(customer.visitCount);
				});

				// Fallback in case of 404 so that the default ticker is rendered
				this.isTickerPageNameSet = true;

				// Check to see if the `param` query parameter is present
				if (
					typeof CURRENT_QUERY_PARAMS.param === 'string' &&
					CURRENT_QUERY_PARAMS.param.trim() !== '' &&
					!this.cookieService.check('promoId')
				) {
					// Set the promoId cookie
					this.cookieService.set('promoId', CURRENT_QUERY_PARAMS.param, '', '/', '', '');
				}
				// Only execute on the Server
			} else {
				/*
				 * Canonical link element
				 */
				// Canonical URL
				const canonicalUrl = `${chain.metaCanonical}${this.router.url.split('?')[0]}`;

				this.createCanonicalTag(canonicalUrl);

				/*
				 * OpenGraph url meta tag
				 */
				this.metaService.addTag({ property: 'og:url', content: canonicalUrl }, true);
			}
		});
	}

	private setStoreInfoFromCookieString(cookieString: string): void {
		const dataFromStoreCookieString =
			this.storeService.getStoreDataFromStoreCookieString(cookieString);

		if (dataFromStoreCookieString?.storeZip || dataFromStoreCookieString?.storeNumber) {
			this.ngrxStore.dispatch(
				setUserStoreInfoFromCookie({
					zipcode: dataFromStoreCookieString.storeZip || '',
					location: dataFromStoreCookieString.storeNumber || '',
					name: dataFromStoreCookieString.storeName || ''
				})
			);
		}
	}

	private setUserZipCodeFromCookie(): void {
		if (!this.sddZipCode) {
			this.sddZipCode =
				this.headerLocationService.getDeliveryZipCookie() ||
				this.headerLocationService.getWhereaboutsCookie();

			this.sddZipCode &&
				this.ngrxStore.dispatch(setUserZipCode({ zipCode: this.sddZipCode }));
		}
	}

	private setStoreInfoZipCodeToStore(): void {
		if (isPlatformBrowser(this.platformId)) {
			// Set the user zip code from the cookie
			this.setUserZipCodeFromCookie();

			/** Get the store cookie string and set the store info
			 */
			const storeCookieString = this.storeService.getSavedCustomerStore();
			if (storeCookieString) {
				this.setStoreInfoFromCookieString(storeCookieString);
			}
		}
	}

	createCanonicalTag(canonicalUrl: string): void {
		// Check to see canonical is already set for plp. To avoid duplicate canonical tag.
		if (
			this.productSelectionStateService.getPageTypeData() === 'plp' &&
			this.document.head.querySelector('[rel="canonical"]')
		) {
			return;
		}

		// Create a link element
		const canonicalLink = this.document.createElement('link');

		// Set the link relationship attribute
		canonicalLink.setAttribute('rel', 'canonical');

		// Set the link href attribute
		canonicalLink.setAttribute('href', canonicalUrl);

		// Append the link element to the head element
		this.document.head.appendChild(canonicalLink);
	}

	private setFamilySRLPAnalyticsData() {
		this.ngrxStore
			.select(selectProductListCore)
			.pipe(take(1))
			.subscribe((plcData: ProductListCore) => {
				const mlSearchEventId = this.plpMlService.getMlSearchEventParentUUID();
				const productList = plcData?.productList;
				const isSearchRedirect = productList?.searchVO?.landingPageURL;

				/**
					If search page redirect to any other page, do not send reporting:PagePostRender.
				*/
				if (isSearchRedirect) {
					return;
				}

				if (productList?.didYouMeanVO) {
					this.window.siteData.DYMTerm = productList?.didYouMeanVO?.suggestions[0];
					this.window.siteData.DYMType = 'DYMAuto';
					this.window.siteData.EnteredSearchTerm =
						productList?.didYouMeanVO?.originalSearchTerm;
				}

				this.window.siteData.FamilyID = plcData.productListSEO?.id;
				this.window.siteData.FamilyName = plcData.productListSEO?.name;
				this.window.siteData.FuzzyMatch = productList?.fuzzyMatch;
				this.window.siteData.LoggedIn = undefined;
				this.window.siteData.MCSalesCatalogDetails = [
					{ code: plcData.productListSEO?.identifier }
				];
				this.window.siteData.MLBypass = !!productList?.mlBypass;
				this.window.siteData.MLGroup = productList?.mlGroup;
				this.window.siteData.MLRank = productList?.mlRank || '';
				this.window.siteData.MLRewrite = productList?.mlRewrite || '';
				this.window.siteData.MLSpelling = !!productList?.mlSpelling;
				this.window.siteData.PLP_event_id = mlSearchEventId;
				this.window.siteData.ProductCountTotal = productList?.totalCount;
				this.window.siteData.ProductPerPage = productList?.searchVO.pageSize;
				this.window.siteData.ProductsInFamily = productList?.totalCount;
				this.window.siteData.SearchData = productList?.searchVO.searchTypes;
				this.window.siteData.SearchResultCount =
					productList?.searchVO.totalCount.toString();
				this.window.siteData.SearchType = this.setSearchType(productList);
				this.window.siteData.SortMethod = analyticsSortMethodValues.find(
					(dropdownOption) => Number(plcData.selectedSort.value) === dropdownOption.value
				)?.text;
				this.window.siteData.ReviewStarsProducts = productList?.productVOs?.filter(
					(product) => product.ratingCount > 0
				).length;

				this.analyticsService.triggerAnalyticsEvent('reporting:PagePostRender');
			});
	}

	setSearchType(productList: ProductList): string {
		const searchTypeDefault = 'Keyword';
		const isFamilyPage = this.productSelectionStateService.getPageTypeData() === 'plp';
		const searchRefinement = this.cookieService.get('searchRefinement');

		if (productList?.totalCount === 0) {
			return '';
		}

		if (isFamilyPage) {
			const sTermCookie = this.cookieService.get('sTermCookie');
			if (sTermCookie) {
				this.window.siteData.EnteredSearchTerm = sTermCookie;
				this.cookieService.delete('sTermCookie', '/', '');
				return 'Redirect';
			}

			if (searchRefinement === 'categories') {
				return 'Suggested Category';
			} else {
				return '';
			}
		}

		if (!productList?.searchVO.landingPageURL && this.cookieService.get('sTermCookie')) {
			this.cookieService.delete('sTermCookie', '/', '');
		}

		//srlp
		if (searchRefinement && searchRefinement.indexOf('Recent') > -1) {
			return 'Suggested History';
		}

		switch (searchRefinement) {
			case 'brands': {
				return 'Suggested Brand';
			}
			case 'enterSearchTerm': {
				return 'Keyword';
			}
			case 'searchTerm': {
				return 'Suggested Keyword';
			}
			case 'recentlySearchedTerm': {
				return 'Suggested Recently Searched';
			}
			default:
				return searchTypeDefault;
		}
	}

	/**
	 * @description use the header service to get athlete's delivery zip code on page load
	 */
	@HostListener('window:HeaderServices:SameDayDelivery:OnLoad', ['$event'])
	setDeliveryZipOnLoad(event) {
		if (event?.detail && event.detail !== this.sddZipCode) {
			this.sddZipCode = event.detail;
			this.ngrxStore.dispatch(setUserZipCode({ zipCode: event.detail }));
		}
	}

	/**
	 * @description use the header service to get athlete's delivery zip code on change
	 */
	@HostListener('window:HeaderServices:SameDayDelivery:Change', ['$event'])
	setDeliveryZipOnChange(event) {
		if (event?.detail && event.detail !== this.sddZipCode) {
			this.sddZipCode = event.detail;
			this.ngrxStore.dispatch(setUserZipCode({ zipCode: event.detail }));
			this.ngrxStore.dispatch(refreshProductList());
		}
	}

	/**
	 * @description Listen for any changes in the header service to the store selection
	 * if there is a change update the state
	 */
	@HostListener('window:HeaderServices:StoreSelection:Change', ['$event'])
	updateStoreDetails(event) {
		if (event?.detail) {
			this.ngrxStore.dispatch(changeHeaderServiceStoreInfo({ storeInfo: event.detail }));
			this.ngrxStore.dispatch(refreshProductList());

			if (event.detail?.name) {
				this.ngrxStore.dispatch(setUserStoreName({ storeName: event.detail.name }));
			}
		}
	}
}
